import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Description,
  Add,
  ExpandMore,
  ChevronRight,
  Segment,
  Download,
  Upload,
  PeopleAlt,
} from '@mui/icons-material';
import sceneService from '../../../../utils/apiRequests/sceneService';
import useThemeStyles from '../../../../hooks/useThemeStyles';
import { getProjectTypeConfig } from '../../../../utils/projectTypeConfig';
import { useProjectAccess } from '../../../../contexts/ProjectAccessContext';

const FileTree = ({
  activeProject = null,
  chapters,
  onAddChapter,
  onChapterClick = null,
  onAddScene,
  onSceneClick = null,
  setRefreshScenesCallback,
  onExport = null,
  onImport = null,
  onShare = null,
}) => {
  const { colors, components } = useThemeStyles();
  const { canEdit, isOwner } = useProjectAccess();
  const typeConfig = getProjectTypeConfig(activeProject?.type);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [scenes, setScenes] = useState({}); // Store scenes by chapterId
  const [loadingScenes, setLoadingScenes] = useState({}); // Track loading state per chapter

  const tooltipComponentsProps = {
    tooltip: {
      sx: {
        backgroundColor: colors.tooltip.background,
        color: colors.tooltip.color,
        fontSize: '0.75rem',
        maxWidth: 300,
        wordWrap: 'break-word',
        boxShadow: components.paper.shadow,
        border: `1px solid ${colors.tooltip.border}`,
      },
    },
  };

  // Helper function to render description with tooltip
  const renderDescription = (scene) => {
    const description = scene.description || scene.data?.text || 'No content';

    return (
      <Tooltip title={description} placement="top" componentsProps={tooltipComponentsProps}>
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            display: 'inline-block',
            color: 'inherit',
          }}
        >
          {description}
        </Box>
      </Tooltip>
    );
  };

  // Function to refresh scenes for a specific chapter
  const refreshScenesForChapter = async (chapterId) => {
    setLoadingScenes((prev) => ({ ...prev, [chapterId]: true }));

    try {
      const response = await sceneService.getScenesByChapter(chapterId);

      if (response?.data?.scenes) {
        setScenes((prev) => ({
          ...prev,
          [chapterId]: response.data.scenes,
        }));
      }
    } catch (error) {
      console.error('Error refreshing scenes for chapter:', chapterId, error);
    } finally {
      setLoadingScenes((prev) => ({ ...prev, [chapterId]: false }));
    }
  };

  // Set up the refresh callback when component mounts
  useEffect(() => {
    if (setRefreshScenesCallback) {
      setRefreshScenesCallback(() => refreshScenesForChapter);
    }
  }, [setRefreshScenesCallback]);

  const toggleChapter = async (chapterId) => {
    const isCurrentlyExpanded = expandedChapters[chapterId];

    setExpandedChapters((prev) => ({
      ...prev,
      [chapterId]: !isCurrentlyExpanded,
    }));

    // If expanding and we don't have scenes for this chapter yet, fetch them
    if (!isCurrentlyExpanded && !scenes[chapterId] && !loadingScenes[chapterId]) {
      await refreshScenesForChapter(chapterId);
    }
  };

  const getScenesForChapter = (chapterId) => {
    return scenes[chapterId] || [];
  };

  const handleAddScene = (chapterId, event) => {
    event.stopPropagation();
    if (onAddScene) {
      onAddScene(chapterId);
    }
  };

  const handleSceneClick = (sceneId, chapterId) => {
    if (onSceneClick) {
      onSceneClick(sceneId, chapterId);
    }
  };

  const handleChapterSelect = (chapter, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (onChapterClick) {
      onChapterClick(chapter);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
        borderRadius: 0,
        p: 0,
        minHeight: '100%',
      }}
    >
      <List dense sx={{ p: 0 }}>
        {/* Project Root */}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: colors.sidebar.headerHover,
              },
            }}
            disabled={!onExport}
            onClick={(event) => {
              event.stopPropagation();
              if (typeof onExport === 'function') {
                onExport();
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Download
                sx={{
                  color: colors.interactive.primary,
                  fontSize: '1.125rem',
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={activeProject?.title || 'Untitled Project'}
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: colors.text.primary,
              }}
              secondary="Export… (Word, Markdown, HTML, text)"
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: colors.text.secondary,
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Import Document (editors and owners only) */}
        {canEdit && (
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: colors.sidebar.headerHover,
                },
              }}
              disabled={!onImport}
              onClick={(event) => {
                event.stopPropagation();
                if (typeof onImport === 'function') {
                  onImport();
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Upload
                  sx={{
                    color: colors.interactive.primary,
                    fontSize: '1.125rem',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Import Document"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: colors.text.primary,
                }}
                secondary="Word (.docx)"
                secondaryTypographyProps={{
                  fontSize: '0.75rem',
                  color: colors.text.secondary,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {/* Share / collaborators */}
        {onShare && (
          <ListItem disablePadding sx={{ mb: 1.5 }}>
            <ListItemButton
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: colors.sidebar.headerHover,
                },
              }}
              onClick={(event) => {
                event.stopPropagation();
                onShare();
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PeopleAlt
                  sx={{
                    color: colors.interactive.primary,
                    fontSize: '1.125rem',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={isOwner ? 'Share…' : 'Shared Project'}
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: colors.text.primary,
                }}
                secondary={isOwner ? 'Invite viewers, commenters, editors' : 'See collaborators'}
                secondaryTypographyProps={{
                  fontSize: '0.75rem',
                  color: colors.text.secondary,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {/* Chapters */}
        <ListItem disablePadding sx={{ pl: 0 }}>
          <List dense sx={{ width: '100%' }}>
            {chapters.map((chapter, index) => {
              const isExpanded = expandedChapters[chapter.id];
              const chapterScenes = getScenesForChapter(chapter.id);
              const chapterDescription = (chapter.description || '').trim();
              const hasDescription = chapterDescription.length > 0;

              const chapterButton = (
                <ListItemButton
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: colors.sidebar.headerHover,
                    },
                  }}
                  onClick={(event) => handleChapterSelect(chapter, event)}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleChapter(chapter.id);
                      }}
                      sx={{
                        color: colors.text.secondary,
                        p: 0.25,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: colors.text.primary,
                        },
                      }}
                    >
                      {isExpanded ? (
                        <ExpandMore sx={{ fontSize: '1.125rem' }} />
                      ) : (
                        <ChevronRight sx={{ fontSize: '1.125rem' }} />
                      )}
                    </IconButton>
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Description
                      sx={{
                        color: colors.interactive.primary,
                        fontSize: '1.125rem',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={chapter.name}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.primary,
                    }}
                  />
                </ListItemButton>
              );

              return (
                <React.Fragment key={chapter.id || index}>
                  {/* Chapter */}
                  <ListItem disablePadding>
                    {hasDescription ? (
                      <Tooltip
                        title={chapterDescription}
                        placement="top"
                        componentsProps={tooltipComponentsProps}
                      >
                        {chapterButton}
                      </Tooltip>
                    ) : (
                      chapterButton
                    )}
                  </ListItem>

                  {/* Scenes */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense>
                      {loadingScenes[chapter.id] && (
                        <ListItem disablePadding>
                          <ListItemButton
                            sx={{
                              pl: 5,
                              py: 1,
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                            }}
                          >
                            <CircularProgress
                              size={14}
                              sx={{
                                mr: 1.5,
                                color: colors.interactive.primary,
                              }}
                            />
                            <ListItemText
                              primary={`Loading ${typeConfig.subUnitPlural.toLowerCase()}...`}
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                color: colors.text.secondary,
                                fontStyle: 'italic',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )}
                      {!loadingScenes[chapter.id] && chapterScenes.length > 0 && (
                        <>
                          {chapterScenes.map((scene) => (
                            <ListItem key={scene._id || scene.id} disablePadding sx={{ py: 0 }}>
                              <ListItemButton
                                sx={{
                                  py: 0.5,
                                  pl: 5,
                                  borderRadius: '6px',
                                  '&:hover': {
                                    backgroundColor: colors.sidebar.headerHover,
                                  },
                                }}
                                onClick={() => handleSceneClick(scene._id || scene.id, chapter.id)}
                              >
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <Segment
                                    sx={{
                                      color: colors.text.secondary,
                                      fontSize: '0.875rem',
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={scene.name?.trim() ? scene.name : scene.type || 'Scene'}
                                  secondary={renderDescription(scene)}
                                  primaryTypographyProps={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 400,
                                    color: colors.text.primary,
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '0.75rem',
                                    color: colors.text.secondary,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </>
                      )}
                      {!loadingScenes[chapter.id] && chapterScenes.length === 0 && (
                        <ListItem disablePadding>
                          <ListItemButton
                            sx={{
                              pl: 5,
                              py: 0.75,
                            }}
                          >
                            <ListItemText
                              primary={`No ${typeConfig.subUnitPlural.toLowerCase()} yet`}
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                color: colors.text.secondary,
                                fontStyle: 'italic',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )}

                      {/* Add Scene Button */}
                      {canEdit && (
                        <ListItem disablePadding sx={{ mb: 1.5 }}>
                          <ListItemButton
                            sx={{
                              pl: 5,
                              py: 0.75,
                              borderRadius: '6px',
                              border: `1px dashed ${colors.border.subtle}`,
                              '&:hover': {
                                backgroundColor: colors.sidebar.headerHover,
                                borderColor: colors.interactive.primary,
                              },
                            }}
                            onClick={(e) => handleAddScene(chapter.id, e)}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Add
                                sx={{
                                  color: colors.interactive.primary,
                                  fontSize: '1rem',
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Add ${typeConfig.subUnitLabel}`}
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                color: colors.interactive.primary,
                                fontWeight: 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}

            {/* Add Chapter Button */}
            {canEdit && (
              <ListItem disablePadding sx={{ mt: 0.5 }}>
                <ListItemButton
                  sx={{
                    px: 1.5,
                    py: 1,
                    border: `1px dashed ${colors.border.subtle}`,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: colors.sidebar.headerHover,
                      borderColor: colors.interactive.primary,
                    },
                  }}
                  onClick={onAddChapter}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Add
                      sx={{
                        color: colors.interactive.primary,
                        fontSize: '1.125rem',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Add ${typeConfig.unitLabel}`}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      color: colors.interactive.primary,
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </ListItem>
      </List>
    </Box>
  );
};

/* eslint-disable react/require-default-props */
FileTree.propTypes = {
  activeProject: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
  }),
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      description: PropTypes.string,
    })
  ).isRequired,
  onAddChapter: PropTypes.func.isRequired,
  onChapterClick: PropTypes.func,
  onAddScene: PropTypes.func.isRequired,
  onSceneClick: PropTypes.func,
  setRefreshScenesCallback: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  onImport: PropTypes.func,
  onShare: PropTypes.func,
};
/* eslint-enable react/require-default-props */

export default FileTree;
